import React, { useCallback, useMemo, useState } from "react";

import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { RoomJobCustomerPortal } from "../../../../../../../models/customer.portal";
import { ItemJob } from "../../../../../../../models/sales";
import groupBy from "../../../../../../../util/groupBy";
import { AfterRoomRow } from "../AfterRoomRow";
import { EmptyState } from "../EmptyState";
import { RoomItemsMobile } from "../RoomItemsMobile";
import { RoomRow } from "../RoomRow";
import { GridContainer, LoadingWrapper, LoadingSpinner, RoomsContainerMobile } from "./styles";

interface ItemsJobsGroupedByRoom {
  [x: string]: ItemJob[];
}

interface ActualInventoryProps {
  itemsJobs?: ItemJob[];
  isFetching?: boolean;
}

const ActualInventory = ({ itemsJobs, isFetching }: ActualInventoryProps) => {
  const [collapseds, setCollapseds] = useState<string[]>([]);

  const { t: translate } = useTranslation();

  const itemsJobsGroupedByRoom = useMemo(() => {
    const data = itemsJobs ? [...itemsJobs] : [];
    const grouped: ItemsJobsGroupedByRoom = groupBy(data, "room.name");

    const roomsItems: RoomJobCustomerPortal[] = Object.keys(grouped).map(
      (room) =>
        ({
          name: room,
          itemsJob: grouped[room] || [],
        } as RoomJobCustomerPortal)
    );

    return roomsItems;
  }, [itemsJobs]);

  const toggleCollapse = useCallback((room?: string) => {
    if (room) {
      setCollapseds((prev) => {
        if (prev.find((item) => item === room)) {
          return prev.filter((item) => item !== room);
        }

        return [...prev, room];
      });
    }
  }, []);

  const renderAfterRow = (data?: RoomJobCustomerPortal) => {
    const room = data?.name;

    if (!room || !collapseds.find((item) => item === room)) return null;

    return (
      <AfterRoomRow roomItemsJobs={data?.itemsJob || []} roomMaterials={data?.materials || []} />
    );
  };

  const columns: Column[] = [
    {
      name: "name",
      title: translate("rooms"),
      hideSort: true,
      formatComponent: (roomName?: string, room?: RoomJobCustomerPortal) => (
        <RoomRow
          collapseds={collapseds}
          toggleCollapse={toggleCollapse}
          room={room}
          roomName={roomName}
        />
      ),
    },
  ];

  if (isFetching) {
    return (
      <LoadingWrapper>
        <LoadingSpinner size={48} disableShrink />
      </LoadingWrapper>
    );
  }

  return (
    <>
      {itemsJobsGroupedByRoom?.length ? (
        <>
          <GridContainer>
            <ShipeeziNewGrid
              data={itemsJobsGroupedByRoom}
              columns={columns}
              loadingData={isFetching}
              renderAfterRow={renderAfterRow}
              showSelection={false}
            />
          </GridContainer>
          <RoomsContainerMobile>
            {itemsJobsGroupedByRoom?.map((room, index) => {
              return (
                <RoomItemsMobile
                  key={`room-${index}`}
                  roomName={room.name}
                  roomItems={room.itemsJob || []}
                />
              );
            })}
          </RoomsContainerMobile>
        </>
      ) : (
        <EmptyState
          title={translate("noItemsHaveBeenRegisteredYet")}
          description={translate("onceItemsAreAddedToInventoryYouWillBeAbleToSeeTheirDetails")}
        />
      )}
    </>
  );
};

export default ActualInventory;
